/**
 * Transfer Hub airline booking payload helpers.
 * Miles & More is captured on the airline landing page and stored in router/session state.
 */

import { readPersistedAirlineRouterState, type TransportAirlineApiContext } from "./airlineTransportContext";

type AirlineRouterState = {
  shared?: {
    milesAndMore?: string;
  };
};

/** Reads the Miles & More number from airline navigation state (location.state or session fallback). */
export function readAirlineMilesAndMoreNumber(locationState?: unknown): string {
  const state = (locationState ?? readPersistedAirlineRouterState()) as AirlineRouterState | null;
  const raw = state?.shared?.milesAndMore;
  return typeof raw === "string" ? raw.trim() : "";
}

/** True when the user entered a Miles & More number in the Transfer Hub airline flow. */
export function hasAirlineMilesAndMoreNumber(locationState?: unknown): boolean {
  return Boolean(readAirlineMilesAndMoreNumber(locationState));
}

/**
 * Miles & More UI is shown when miles exist and, for airline Transfer Hub,
 * the user provided an M&M number during booking.
 */
export function shouldShowMilesAndMore(
  miles: string | undefined | null,
  isAirlineFlow: boolean,
  locationState?: unknown
): boolean {
  if (!miles) {
    return false;
  }

  if (!isAirlineFlow) {
    return true;
  }

  return hasAirlineMilesAndMoreNumber(locationState);
}

export type AirlineMilesAndMoreBookingFields = {
  miles_and_more_number?: string;
};

/**
 * Builds optional `miles_and_more_number` for airline (Transfer Hub) transport booking payloads.
 * Omitted for hotel flows and when the user did not enter a number.
 */
export function buildAirlineMilesAndMoreBookingFields(
  transportContext: TransportAirlineApiContext,
  options?: {
    milesAndMoreNumber?: string | null;
    locationState?: unknown;
  }
): AirlineMilesAndMoreBookingFields {
  if (transportContext.mode !== "airline") {
    return {};
  }

  const explicit = options?.milesAndMoreNumber;
  const number =
    typeof explicit === "string" && explicit.trim()
      ? explicit.trim()
      : readAirlineMilesAndMoreNumber(options?.locationState);

  if (!number) {
    return {};
  }

  return { miles_and_more_number: number };
}
